import React from 'react';
import type { PatternUnitProps } from './PatternUnit';
import { PatternUnit } from './PatternUnit';
import { PatternLine } from './PatternLine';
import { TestIds } from '../../constants';
import type { TextEffectsSharedData } from '../../TextEffectsShared.types';

export interface PatternsProps {
  content: Array<Array<{ data: string; isVisible: boolean }>>;
  unitProps?: (data: string) => Omit<PatternUnitProps, 'patternScope'>;
  unitPredicate?: (data: string) => React.ReactNode;
  arePatternsDisabled?: boolean;
  patternScope: TextEffectsSharedData['patternScope'];
}

export const Patterns: React.FC<PatternsProps> = ({
  content,
  unitProps = () => ({}),
  unitPredicate = data => data,
  arePatternsDisabled = false,
  patternScope,
}) => {
  const lines = content.length;

  // When patterns are enabled with 'character' scope,
  // parseGrapheme.ts uses bidi-js for splitting the text and reordering it,
  // and assumes the text is LTR, so we need to use a span with dir="ltr" to avoid interfering with it
  // even if the component is set to RTL
  const Wrapper =
    !arePatternsDisabled &&
    (patternScope === 'character' || patternScope === 'word')
      ? 'span'
      : React.Fragment;
  const wrapperProps =
    Wrapper === 'span'
      ? { 'aria-hidden': true, style: { display: 'block' }, dir: 'ltr' }
      : {};

  return (
    <Wrapper {...wrapperProps}>
      {content.map((line, i) => (
        <PatternLine
          key={`${line.join('')}-${i}`}
          arePatternsDisabled={arePatternsDisabled}
          isLastLine={i === lines - 1}
        >
          {line.map(({ data, isVisible }, j) =>
            !isVisible ? (
              // Use b instead of span to avoid interfering with nth-of-type selectors in PatternUnit.scss
              // Invisible spans were being counted in nth-of-type calculations, causing wrong pattern targeting
              // See: https://github.com/wix-private/editor-elements/pull/23826
              <b
                style={{ fontWeight: 'normal' }}
                {...(!arePatternsDisabled && { 'aria-hidden': true })}
              >
                {data}
              </b>
            ) : (
              <PatternUnit
                key={`${data}-${j}`}
                data-testid={TestIds.viewer.patternUnit}
                {...unitProps(data)}
                {...(!arePatternsDisabled && { ariaHidden: true })}
                patternScope={patternScope}
              >
                {unitPredicate(data)}
              </PatternUnit>
            ),
          )}
        </PatternLine>
      ))}
    </Wrapper>
  );
};
